import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { EarTag, useFormatEarTag } from "../../components/tags/EarTag";
import { SortHeader } from '../../components/forms/SortHeader';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { TagsSubnav } from '../../components/subnav/TagsSubnav';
import { apiError } from "../../lib/apiError";
import {
  ALL_PROGRAM_STATUSES,
  formatDateTime,
  normalizeTag,
  PROGRAM_STATUS_LABELS,
} from "../../lib/format";
import { useModalHistoryEntry } from "../../lib/modalHistory";
import { useFilterFieldProps } from "../../lib/inputPrivacy";
import { useTableControls } from "../../lib/tableControls";
import {
  AUTO_PLACEHOLDER,
  MANUAL_PREFIX,
  formatTagForMode,
  matchActiveTag,
  protectManualPrefixValue,
  tagMatchesFilter,
  typedTagFilterQuery,
  type TagInputMode,
} from "../../lib/tagVerificationInput";
import {
  loadOfflineSession,
  saveOfflineSession,
  clearOfflineSession,
  type OfflineTagVerificationSession,
} from "../../lib/offline/offlineTagVerification";
import { loadFromReplica } from "../../lib/offline/replica";
import { animalsListFromReplica } from "../../lib/offline/sanitize";
import type {
  ActiveNotFoundItem,
  ActiveTagItem,
  ActiveTagVerification,
  ProgramStatus,
} from "../../types";

type Phase = "start" | "verify";
type FinishStep = 1 | 2 | 3;
type ControlActivityId = "tag-verification" | "weighing" | "vaccination" | "deworming";

type ControlActivity = {
  id: ControlActivityId;
  label: string;
  description: string;
  available: boolean;
};

const CONTROL_ACTIVITIES: ControlActivity[] = [
  {
    id: "tag-verification",
    label: "Weryfikacja kolczyków",
    description: "Odhaczanie numerów w terenie i sprawdzenie liczby kolczyków.",
    available: true,
  },
  {
    id: "weighing",
    label: "Ważenie matek",
    description: "Wkrótce — zapis wag podczas kontroli stada.",
    available: false,
  },
  {
    id: "vaccination",
    label: "Szczepienia",
    description: "Wkrótce — odhaczenie szczepień w terenie.",
    available: false,
  },
  {
    id: "deworming",
    label: "Odrobaczanie",
    description: "Wkrótce — odhaczenie odrobaczania w terenie.",
    available: false,
  },
];

type TagRow = ActiveTagItem & { number: number };

function tagVerificationValue(item: TagRow, key: string): unknown {
  switch (key) {
    case "tag":
      return item.tag;
    case "number":
      return item.number;
    case "tag_count":
      return item.tag_count;
    case "status":
      return item.verified ? "zweryfikowany" : "niezweryfikowany";
    case "verified_at":
      return item.verified_at;
    default:
      return null;
  }
}

function sessionFromOffline(offline: OfflineTagVerificationSession): ActiveTagVerification {
  return {
    id: offline.server_session_id || 0,
    started_at: new Date(offline.created_at).toISOString(),
    updated_at: new Date(offline.updated_at).toISOString(),
    program_statuses: offline.program_statuses,
    tags: offline.tags,
    not_found: offline.not_found,
  };
}

function toSession(data: unknown): ActiveTagVerification | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  if ("session" in data) {
    return (data as { session: ActiveTagVerification | null }).session ?? null;
  }
  return data as ActiveTagVerification;
}

function sourceLabel(source: string | null): string {
  if (source === "auto") {
    return "Automatycznie";
  }
  if (source === "manual") {
    return "Ręcznie";
  }
  if (source === "restored") {
    return "Przywrócone";
  }
  return source || "—";
}

export function TagVerificationPage() {
  const formatTag = useFormatEarTag();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("start");
  const [session, setSession] = useState<ActiveTagVerification | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tagFilterProps = useFilterFieldProps("tag");

  const [selectedStatuses, setSelectedStatuses] = useState<ProgramStatus[]>([
    ...ALL_PROGRAM_STATUSES,
  ]);
  const [activityId, setActivityId] = useState<ControlActivityId>("tag-verification");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterRestart, setFilterRestart] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  const [mode, setMode] = useState<TagInputMode>("manual");
  const [tag, setTag] = useState(MANUAL_PREFIX);

  const [earTagsOpen, setEarTagsOpen] = useState(false);
  const [pendingTag, setPendingTag] = useState<ActiveTagItem | null>(null);

  const [clearOpen, setClearOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<ActiveTagItem | null>(null);
  const [editCount, setEditCount] = useState(2);
  const [editAction, setEditAction] = useState<"count" | "reset" | null>(null);

  const [finishOpen, setFinishOpen] = useState(false);
  const [finishStep, setFinishStep] = useState<FinishStep>(1);
  const [finishTitle, setFinishTitle] = useState("");
  const [finishDescription, setFinishDescription] = useState("");

  async function loadActive(preferStart = true) {
    const applySession = (next: ActiveTagVerification | null) => {
      setSession(next);
      if (!next || preferStart) {
        setPhase("start");
      }
    };

    if (!navigator.onLine) {
      const offlineSession = await loadOfflineSession();
      setError(null);
      applySession(offlineSession ? sessionFromOffline(offlineSession) : null);
      return;
    }

    const result = await api.GET("/api/tag-verifications/active");
    if (result.error) {
      const offlineSession = await loadOfflineSession();
      if (offlineSession) {
        applySession(sessionFromOffline(offlineSession));
        return;
      }
      setError(apiError(result.error, "Nie udało się wczytać sesji weryfikacji."));
      if (preferStart) {
        setPhase("start");
      }
      return;
    }

    setError(null);
    const data = toSession(result.data);
    applySession(data);
    if (!data) {
      await clearOfflineSession();
      return;
    }

    await saveOfflineSession({
      program_statuses: data.program_statuses,
      tags: data.tags,
      not_found: data.not_found,
      created_at: Date.now(),
      updated_at: Date.now(),
      synced: true,
      server_session_id: data.id,
    });
  }

  useEffect(() => {
    void loadActive(true);
  }, []);

  useModalHistoryEntry(filterOpen, () => setFilterOpen(false));
  useModalHistoryEntry(earTagsOpen, () => {
    setEarTagsOpen(false);
    setPendingTag(null);
  });
  useModalHistoryEntry(clearOpen, () => setClearOpen(false));
  useModalHistoryEntry(editOpen, () => {
    setEditOpen(false);
    setEditRecord(null);
  });
  useModalHistoryEntry(editConfirmOpen, () => {
    setEditConfirmOpen(false);
    setEditAction(null);
  });
  useModalHistoryEntry(finishOpen, () => {
    setFinishOpen(false);
    setFinishStep(1);
  });

  const verifiedCount = session?.tags.filter((item) => item.verified).length ?? 0;
  const unverifiedCount = (session?.tags.length ?? 0) - verifiedCount;
  const checkQuery = typedTagFilterQuery(mode, tag);

  const tagRows = useMemo<TagRow[]>(() => {
    const rows = (session?.tags ?? []).map((item, index) => ({ ...item, number: index + 1 }));
    if (!checkQuery) {
      return rows;
    }
    return rows.filter((item) => tagMatchesFilter(item.tag, checkQuery));
  }, [checkQuery, session?.tags]);

  const {
    view: filteredTags,
    sortKey,
    sortDir,
    filters,
    toggleSort,
    setFilter,
    clearFilters,
  } = useTableControls({
    rows: tagRows,
    getValue: tagVerificationValue,
    defaultSortKey: "tag",
    defaultSortDir: "asc",
  });

  const statusFilter =
    filters.status === "zweryfikowany"
      ? "verified"
      : filters.status === "niezweryfikowany"
        ? "unverified"
        : "all";

  function openFilter(restart: boolean) {
    setFilterRestart(restart);
    setSelectedStatuses([...ALL_PROGRAM_STATUSES]);
    setFilterError(null);
    setFilterOpen(true);
  }

  async function startSession() {
    if (selectedStatuses.length === 0) {
      setFilterError("Wybierz co najmniej jeden status programu.");
      return;
    }
    setBusy(true);
    setStatus(null);

    // Jeśli offline, utwórz sesję z lokalnej repliki
    if (!navigator.onLine) {
      const animalsData = await loadFromReplica("animals:active");
      const replicaAnimals = animalsListFromReplica(animalsData);
      if (replicaAnimals.length === 0) {
        setBusy(false);
        setError("Brak lokalnych danych zwierząt. Połącz się z internetem aby rozpocząć weryfikację.");
        return;
      }

      // Filtruj według wybranych statusów
      const filteredAnimals = replicaAnimals.filter((animal) =>
        selectedStatuses.includes((animal.program_status as ProgramStatus) ?? "unspecified"),
      );

      const tags: ActiveTagItem[] = filteredAnimals.map(animal => ({
        tag: animal.tag,
        normalized: normalizeTag(animal.tag),
        program_status: (animal.program_status as ProgramStatus) ?? "unspecified",
        verified: false,
        tag_count: 1,
        verified_at: null,
      }));

      await saveOfflineSession({
        program_statuses: selectedStatuses,
        tags,
        not_found: [],
        created_at: Date.now(),
        updated_at: Date.now(),
        synced: false,
      });

      setBusy(false);
      setError(null);
      setFilterOpen(false);
      setSession({
        id: 0,
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        program_statuses: selectedStatuses,
        tags,
        not_found: [],
      });
      setPhase("verify");
      setMode("manual");
      setTag(MANUAL_PREFIX);
      clearFilters();
      setStatus("Sesja utworzona offline. Zostanie zsynchronizowana po powrocie sieci.");
      return;
    }

    // Online - standardowy POST
    const started = await api.POST("/api/tag-verifications/active", {
      body: { program_statuses: selectedStatuses },
    });
    setBusy(false);
    if (started.error) {
      setError(apiError(started.error, "Nie udało się rozpocząć weryfikacji."));
      return;
    }
    setError(null);
    setFilterOpen(false);
    const data = toSession(started.data);
    setSession(data);
    setPhase("verify");
    setMode("manual");
    setTag(MANUAL_PREFIX);
    clearFilters();

    // Zapisz do offline store
    if (data) {
      await saveOfflineSession({
        program_statuses: selectedStatuses,
        tags: data.tags,
        not_found: data.not_found,
        created_at: Date.now(),
        updated_at: Date.now(),
        synced: true,
      });
    }
  }

  async function saveSession(tags: ActiveTagItem[], notFound: ActiveNotFoundItem[]) {
    setBusy(true);

    // Zawsze zapisz lokalnie
    const offlineSession = await loadOfflineSession();
    await saveOfflineSession({
      program_statuses: offlineSession?.program_statuses || [...ALL_PROGRAM_STATUSES],
      tags,
      not_found: notFound,
      created_at: offlineSession?.created_at || Date.now(),
      updated_at: Date.now(),
      synced: false,
      server_session_id: offlineSession?.server_session_id,
    });

    // Jeśli offline, zwróć lokalną sesję
    if (!navigator.onLine) {
      setBusy(false);
      const next: ActiveTagVerification = {
        id: offlineSession?.server_session_id || 0,
        started_at: offlineSession?.created_at ? new Date(offlineSession.created_at).toISOString() : new Date().toISOString(),
        updated_at: new Date().toISOString(),
        program_statuses: offlineSession?.program_statuses || [...ALL_PROGRAM_STATUSES],
        tags,
        not_found: notFound,
      };
      setSession(next);
      setStatus("Zmiany zapisane lokalnie. Zostaną zsynchronizowane po powrocie sieci.");
      return next;
    }

    // Online - zapisz na serwerze
    const saved = await api.PUT("/api/tag-verifications/active", {
      body: { tags, not_found: notFound },
    });
    setBusy(false);
    if (saved.error) {
      setError(apiError(saved.error, "Nie udało się zapisać na serwerze. Zmiany zachowane lokalnie."));
      // Zwróć lokalną sesję mimo błędu serwera
      const next: ActiveTagVerification = {
        id: offlineSession?.server_session_id || 0,
        started_at: offlineSession?.created_at ? new Date(offlineSession.created_at).toISOString() : new Date().toISOString(),
        updated_at: new Date().toISOString(),
        program_statuses: offlineSession?.program_statuses || [...ALL_PROGRAM_STATUSES],
        tags,
        not_found: notFound,
      };
      setSession(next);
      return next;
    }
    setError(null);
    
    // Oznacz jako zsynchronizowaną
    await saveOfflineSession({
      program_statuses: offlineSession?.program_statuses || [...ALL_PROGRAM_STATUSES],
      tags,
      not_found: notFound,
      created_at: offlineSession?.created_at || Date.now(),
      updated_at: Date.now(),
      synced: true,
      server_session_id: offlineSession?.server_session_id,
    });

    const next = toSession(saved.data);
    setSession(next);
    return next;
  }

  function resetInputAfterCheck() {
    if (mode === "manual") {
      setTag(MANUAL_PREFIX);
    } else {
      setTag("");
    }
  }

  async function applyVerification(record: ActiveTagItem, tagCount: 1 | 2) {
    if (!session) {
      return;
    }
    const now = new Date().toISOString();
    const tags = session.tags.map((item) =>
      item.normalized === record.normalized
        ? { ...item, verified: true, tag_count: tagCount, verified_at: now }
        : item,
    );
    const saved = await saveSession(tags, session.not_found);
    if (!saved) {
      return;
    }
    // Po skanie pokaż pełną listę — aktywny filtr statusu chowałby właśnie
    // zweryfikowany wpis (i myląco wyglądał jak „Brak pozycji dla filtra”).
    setFilter("status", "");
    setStatus(`Zweryfikowano: ${formatTag(record.tag)} (${tagCount})`);
    resetInputAfterCheck();
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!session) {
      setError("Najpierw rozpocznij weryfikację.");
      return;
    }
    const raw = formatTagForMode(mode, tag);
    const normalized = normalizeTag(raw);
    if (!normalized) {
      setError("Podaj numer kolczyka.");
      return;
    }
    const found = matchActiveTag(session.tags, raw);
    if (found) {
      if (found.verified) {
        setStatus(`Już zweryfikowany: ${formatTag(found.tag)}`);
        resetInputAfterCheck();
        return;
      }
      setPendingTag(found);
      setEarTagsOpen(true);
      return;
    }
    const query = typedTagFilterQuery(mode, tag);
    const partialMatches = query
      ? session.tags.filter((item) => tagMatchesFilter(item.tag, query))
      : [];
    if (partialMatches.length > 1) {
      setError("Znaleziono kilka kolczyków — doprecyzuj numer.");
      return;
    }
    const notFound = session.not_found.some((item) => item.normalized === normalized)
      ? session.not_found
      : [
          ...session.not_found,
          {
            tag: raw,
            normalized,
            source: mode === "auto" ? "auto" : "manual",
          },
        ];
    await saveSession(session.tags, notFound);
    setStatus("Nie znaleziono na liście");
    resetInputAfterCheck();
  }

  async function clearSession() {
    setBusy(true);
    const cleared = await api.DELETE("/api/tag-verifications/active");
    setBusy(false);
    if (cleared.error) {
      setError(apiError(cleared.error, "Nie udało się wyczyścić sesji."));
      return;
    }
    setClearOpen(false);
    setSession(null);
    setPhase("start");
    setStatus("Wyczyszczono dane sesji.");
    await clearOfflineSession();
  }

  async function confirmEdit() {
    if (!session || !editRecord || !editAction) {
      return;
    }
    const tags = session.tags.map((item) => {
      if (item.normalized !== editRecord.normalized) {
        return item;
      }
      if (editAction === "reset") {
        return { ...item, verified: false, tag_count: null, verified_at: null };
      }
      return { ...item, tag_count: editCount };
    });
    await saveSession(tags, session.not_found);
    setEditConfirmOpen(false);
    setEditOpen(false);
    setEditRecord(null);
    setEditAction(null);
    setStatus(`Zaktualizowano wpis: ${formatTag(editRecord.tag)}`);
  }

  async function finishSession() {
    if (!session) {
      return;
    }
    const trimmed = finishTitle.trim();
    if (!trimmed) {
      setError("Podaj tytuł sesji weryfikacji.");
      return;
    }

    // Jeśli offline, zapisz w outbox do późniejszej synchronizacji
    if (!navigator.onLine) {
      setError("Zakończenie sesji wymaga połączenia z internetem. Sesja zostanie zachowana lokalnie.");
      return;
    }

    setBusy(true);
    const finished = await api.POST("/api/tag-verifications/finish", {
      body: {
        title: trimmed,
        description: finishDescription.trim() || null,
        tags: session.tags.map((item) => ({
          tag: item.tag,
          verified: item.verified,
          tag_count: item.tag_count,
          verified_at: item.verified_at,
        })),
        not_found: session.not_found.map((item) => ({
          tag: item.tag,
          source: item.source,
        })),
      },
    });
    setBusy(false);
    if (finished.error) {
      setError(apiError(finished.error, "Nie udało się zakończyć sesji."));
      return;
    }

    // Wyczyść lokalną sesję po udanym finish
    await clearOfflineSession();

    const data = finished.data as { session_id: number };
    // replace — nie zamykaj modalu przez setFinishOpen(false): popFromUi
    // robi history.back() i cofa tę nawigację (zostaje ekran startu kontroli).
    navigate(`/controls/history?session=${data.session_id}`, { replace: true });
  }

  const scopeLabel =
    session?.program_statuses
      .map((value) => PROGRAM_STATUS_LABELS[value] || value)
      .join(", ") || "";

  return (
    <main className="page" data-testid="tags-page">
      <header className="page__header page__header--row">
        <div>
          <p className="page__kicker">Kontrole</p>
          <h1>Kontrola stada</h1>
        </div>
      </header>
      <TagsSubnav />

      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <div data-testid="tags-status">
        <AlertBanner variant="ok" message={status} onDismiss={() => setStatus(null)} />
      </div>

      {phase === "start" ? (
        <section className="panel" data-testid="tags-start-panel">
          {session ? (
            <>
              <h2 className="panel__title">Niedokończona kontrola</h2>
              <p className="muted">Weryfikacja kolczyków</p>
              <ul className="muted" data-testid="tags-resume-summary">
                <li>Ostatnia aktualizacja: {formatDateTime(session.updated_at)}</li>
                <li>Na liście: {session.tags.length}</li>
                <li>Zweryfikowany: {verifiedCount}</li>
                <li>Niezweryfikowany: {unverifiedCount}</li>
                <li>Niezarejestrowane: {session.not_found.length}</li>
              </ul>
              <div className="actions">
                <button
                  className="btn"
                  type="button"
                  data-testid="tags-continue"
                  disabled={busy}
                  onClick={() => {
                    setPhase("verify");
                    setStatus(null);
                  }}
                >
                  Kontynuuj kontrolę
                </button>
                <button
                  className="btn btn--ghost"
                  type="button"
                  data-testid="tags-restart"
                  disabled={busy}
                  onClick={() => openFilter(true)}
                >
                  Rozpocznij od nowa
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="muted">
                Wybierz rodzaj czynności kontrolnej w stadzie. Na razie dostępna jest weryfikacja
                kolczyków — kolejne opcje pojawią się później.
              </p>
              <fieldset className="control-types" data-testid="control-activity-picker">
                <legend className="control-types__legend">Rodzaj kontroli</legend>
                {CONTROL_ACTIVITIES.map((activity) => {
                  const selected = activityId === activity.id;
                  return (
                    <label
                      key={activity.id}
                      className={
                        activity.available
                          ? selected
                            ? "control-type control-type--selected"
                            : "control-type"
                          : "control-type control-type--soon"
                      }
                    >
                      <input
                        type="radio"
                        name="control-activity"
                        value={activity.id}
                        checked={selected}
                        disabled={!activity.available}
                        data-testid={`control-activity-${activity.id}`}
                        onChange={() => setActivityId(activity.id)}
                      />
                      <span className="control-type__body">
                        <span className="control-type__label">
                          {activity.label}
                          {!activity.available ? (
                            <span className="control-type__badge">Wkrótce</span>
                          ) : null}
                        </span>
                        <span className="control-type__desc">{activity.description}</span>
                      </span>
                    </label>
                  );
                })}
              </fieldset>
              <button
                className="btn"
                type="button"
                data-testid="tags-start"
                disabled={busy || activityId !== "tag-verification"}
                onClick={() => openFilter(false)}
              >
                Rozpocznij kontrolę
              </button>
            </>
          )}
        </section>
      ) : null}

      {phase === "verify" && session ? (
        <>
          <p className="muted" data-testid="tags-scope">
            Weryfikacja kolczyków · status programu: {scopeLabel || "—"}
          </p>

          <form className="panel form" onSubmit={onSubmit} data-testid="tags-check-form">
            <p className="muted">
              Zweryfikowano {verifiedCount} z {session.tags.length}. Niezarejestrowane:{" "}
              {session.not_found.length}.
            </p>
            <div className="form__row">
              <label className="field">
                <span className="field__label">Tryb wprowadzania</span>
                <select
                  className="input"
                  data-testid="tag-input-mode"
                  value={mode}
                  onChange={(event) => {
                    const next = event.target.value as TagInputMode;
                    setMode(next);
                    setTag(next === "manual" ? MANUAL_PREFIX : "");
                  }}
                >
                  <option value="manual">Ręcznie</option>
                  <option value="auto">Automatycznie</option>
                </select>
              </label>
              <label className="field">
                <span className="field__label">Numer kolczyka</span>
                <input
                  className="input"
                  data-testid="tag-input"
                  value={tag}
                  placeholder={mode === "auto" ? AUTO_PLACEHOLDER : `${MANUAL_PREFIX}…`}
                  autoComplete="off"
                  onChange={(event) => {
                    const value =
                      mode === "manual"
                        ? protectManualPrefixValue(event.target.value)
                        : event.target.value;
                    setTag(value);
                  }}
                  onSelect={(event) => {
                    if (mode !== "manual") {
                      return;
                    }
                    const input = event.currentTarget;
                    if ((input.selectionStart ?? 0) < MANUAL_PREFIX.length) {
                      input.setSelectionRange(MANUAL_PREFIX.length, MANUAL_PREFIX.length);
                    }
                  }}
                />
              </label>
            </div>
            <button className="btn" type="submit" disabled={busy} data-testid="tag-submit">
              Sprawdź numer
            </button>
          </form>

          <section className="panel">
            <div className="panel__header">
              <h2 className="panel__title">Lista</h2>
              <div className="panel__header-actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  data-testid="tags-clear"
                  onClick={() => setClearOpen(true)}
                >
                  Wyczyść dane
                </button>
                <button
                  type="button"
                  className="btn btn--sm"
                  data-testid="tags-finish"
                  onClick={() => {
                    setFinishStep(1);
                    setFinishTitle("");
                    setFinishDescription("");
                    setFinishOpen(true);
                  }}
                >
                  Zakończ kontrolę
                </button>
              </div>
            </div>
            <div className="filters">
              <input
                className="input"
                type="search"
                placeholder="Szukaj numeru…"
                value={filters.tag ?? ""}
                onChange={(event) => setFilter("tag", event.target.value)}
                data-testid="tags-search"
                {...tagFilterProps}
              />
              <div className="actions" role="group" aria-label="Filtr statusu">
                {(
                  [
                    ["all", "Wszystkie", ""],
                    ["verified", "Zweryfikowany", "zweryfikowany"],
                    ["unverified", "Niezweryfikowany", "niezweryfikowany"],
                  ] as const
                ).map(([value, label, filterValue]) => (
                  <button
                    key={value}
                    type="button"
                    className={
                      statusFilter === value ? "btn btn--sm" : "btn btn--ghost btn--sm"
                    }
                    data-testid={`tags-filter-${value}`}
                    onClick={() => setFilter("status", filterValue)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="table-wrap">
              <ResponsiveTable className="tbl" data-testid="tags-table">
                <thead>
                  <tr>
                    <th>
                      <SortHeader
                        label="Lp."
                        sortKey="number"
                        activeKey={sortKey}
                        sortDir={sortDir}
                        onToggle={toggleSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        label="Kolczyk"
                        sortKey="tag"
                        activeKey={sortKey}
                        sortDir={sortDir}
                        onToggle={toggleSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        label="Status"
                        sortKey="status"
                        activeKey={sortKey}
                        sortDir={sortDir}
                        onToggle={toggleSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        label="Liczba"
                        sortKey="tag_count"
                        activeKey={sortKey}
                        sortDir={sortDir}
                        onToggle={toggleSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        label="Data"
                        sortKey="verified_at"
                        activeKey={sortKey}
                        sortDir={sortDir}
                        onToggle={toggleSort}
                      />
                    </th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTags.length === 0 ? (
                    <tr className="tbl__empty-row">
                      <td colSpan={6} className="muted" data-testid="tags-table-empty">
                        {session.tags.length === 0
                          ? "Brak pozycji na liście weryfikacji."
                          : "Brak pozycji dla filtra."}
                      </td>
                    </tr>
                  ) : (
                    filteredTags.map((item) => (
                      <tr key={item.normalized}>
                        <td>{item.number}</td>
                        <td>
                          <strong>
                            <EarTag value={item.tag} />
                          </strong>
                        </td>
                        <td>{item.verified ? "Zweryfikowany" : "Niezweryfikowany"}</td>
                        <td>{item.tag_count ?? "—"}</td>
                        <td>{formatDateTime(item.verified_at)}</td>
                        <td>
                          {item.verified ? (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              data-testid="tag-edit"
                              onClick={() => {
                                setEditRecord(item);
                                setEditCount(item.tag_count === 1 ? 1 : 2);
                                setEditOpen(true);
                              }}
                            >
                              Edytuj
                            </button>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </ResponsiveTable>
            </div>
          </section>

          <section className="panel">
            <h2 className="panel__title">Niezarejestrowane</h2>
            <div className="table-wrap">
              <ResponsiveTable className="tbl" data-testid="tags-not-found-table">
                <thead>
                  <tr>
                    <th>Kolczyk</th>
                    <th>Źródło</th>
                  </tr>
                </thead>
                <tbody>
                  {session.not_found.length === 0 ? (
                    <tr className="tbl__empty-row">
                      <td colSpan={2} className="muted">
                        Brak — wszystkie zeskanowane numery były na liście.
                      </td>
                    </tr>
                  ) : (
                    session.not_found.map((item) => (
                      <tr key={item.normalized}>
                        <td>
                          <EarTag value={item.tag} />
                        </td>
                        <td>{sourceLabel(item.source)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </ResponsiveTable>
            </div>
          </section>
        </>
      ) : null}

      {filterOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tags-filter-modal-title"
          data-testid="tags-filter-modal"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setFilterOpen(false);
            }
          }}
        >
          <div
            className="modal modal--program-filter"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="modal__title" id="tags-filter-modal-title">
              {filterRestart ? "Rozpocząć weryfikację od nowa?" : "Wybierz owce do weryfikacji"}
            </h3>
            <p className="modal__text">
              Zaznacz statusy programu owiec, które mają trafić na listę weryfikacji.
            </p>
            {filterRestart ? (
              <p className="program-filter__warning" role="status">
                Aktualna, niedokończona sesja weryfikacji zostanie utracona.
              </p>
            ) : null}

            <fieldset
              className={
                filterError
                  ? "program-filter program-filter--modal program-filter--error"
                  : "program-filter program-filter--modal"
              }
            >
              <legend className="program-filter__legend">Status programu</legend>
              <div className="program-filter__toolbar">
                <p className="program-filter__hint">Wybierz co najmniej jedną opcję.</p>
                <div className="program-filter__quick">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setSelectedStatuses([...ALL_PROGRAM_STATUSES])}
                  >
                    Zaznacz wszystkie
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setSelectedStatuses([])}
                  >
                    Odznacz
                  </button>
                </div>
              </div>
              <div className="program-filter__options" role="group" aria-label="Statusy programu">
                {ALL_PROGRAM_STATUSES.map((value) => {
                  const checked = selectedStatuses.includes(value);
                  return (
                    <label
                      key={value}
                      className={
                        checked
                          ? "program-filter__option is-checked"
                          : "program-filter__option"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        data-testid={`tags-status-${value}`}
                        onChange={(event) => {
                          setSelectedStatuses((current) =>
                            event.target.checked
                              ? [...current, value]
                              : current.filter((item) => item !== value),
                          );
                        }}
                      />
                      <span>{PROGRAM_STATUS_LABELS[value]}</span>
                    </label>
                  );
                })}
              </div>
              {filterError ? (
                <p className="program-filter__error" role="alert">
                  {filterError}
                </p>
              ) : null}
            </fieldset>

            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setFilterOpen(false)}>
                Anuluj
              </button>
              <button
                type="button"
                className={filterRestart ? "btn btn--danger" : "btn"}
                data-testid="tags-filter-confirm"
                disabled={busy}
                onClick={() => void startSession()}
              >
                {filterRestart ? "Rozpocznij od nowa" : "Rozpocznij kontrolę"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {earTagsOpen && pendingTag ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" data-testid="tags-ear-modal">
          <div className="modal">
            <h3 className="modal__title">Liczba kolczyków</h3>
            <p className="modal__text">
              Czy owca <strong><EarTag value={pendingTag.tag} /></strong> ma oba kolczyki?
            </p>
            <div className="modal__actions">
              <button
                type="button"
                className="btn"
                data-testid="tags-ear-yes"
                disabled={busy}
                onClick={() => {
                  const record = pendingTag;
                  if (!record) {
                    return;
                  }
                  void applyVerification(record, 2).finally(() => {
                    setEarTagsOpen(false);
                    setPendingTag(null);
                  });
                }}
              >
                Tak (2)
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                data-testid="tags-ear-no"
                disabled={busy}
                onClick={() => {
                  const record = pendingTag;
                  if (!record) {
                    return;
                  }
                  void applyVerification(record, 1).finally(() => {
                    setEarTagsOpen(false);
                    setPendingTag(null);
                  });
                }}
              >
                Nie (1)
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {clearOpen ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" data-testid="tags-clear-modal">
          <div className="modal">
            <h3 className="modal__title">Czy na pewno wyczyścić dane?</h3>
            <p className="modal__text">Aktywna sesja weryfikacji zostanie usunięta.</p>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setClearOpen(false)}>
                Anuluj
              </button>
              <button
                type="button"
                className="btn btn--danger"
                data-testid="tags-clear-confirm"
                disabled={busy}
                onClick={() => void clearSession()}
              >
                Potwierdź
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editOpen && editRecord ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" data-testid="tags-edit-modal">
          <div className="modal">
            <h3 className="modal__title">
              Edytuj: <EarTag value={editRecord.tag} />
            </h3>
            <label className="field">
              <span className="field__label">Liczba kolczyków</span>
              <select
                className="input"
                data-testid="tag-edit-count"
                value={editCount}
                onChange={(event) => setEditCount(Number(event.target.value))}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
              </select>
            </label>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setEditOpen(false);
                  setEditRecord(null);
                }}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn btn--danger"
                data-testid="tag-edit-reset"
                onClick={() => {
                  setEditAction("reset");
                  setEditOpen(false);
                  setEditConfirmOpen(true);
                }}
              >
                Wyczyść status (Niezweryfikowany)
              </button>
              <button
                type="button"
                className="btn"
                data-testid="tag-edit-apply"
                onClick={() => {
                  setEditAction("count");
                  setEditOpen(false);
                  setEditConfirmOpen(true);
                }}
              >
                Zastosuj
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editConfirmOpen && editRecord && editAction ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="tags-edit-confirm-modal"
        >
          <div className="modal">
            <h3 className="modal__title">Potwierdź zmianę</h3>
            <p className="modal__text">
              {editAction === "reset" ? (
                <>
                  Czy na pewno wyczyścić status numeru <EarTag value={editRecord.tag} /> i przywrócić
                  stan „Niezweryfikowany”?
                </>
              ) : (
                <>
                  Czy na pewno zmienić liczbę kolczyków dla <EarTag value={editRecord.tag} /> na{" "}
                  {editCount}?
                </>
              )}
            </p>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setEditConfirmOpen(false);
                  setEditAction(null);
                  setEditOpen(true);
                }}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn"
                data-testid="tag-edit-confirm"
                disabled={busy}
                onClick={() => void confirmEdit()}
              >
                Potwierdź
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {finishOpen && session ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" data-testid="tags-finish-modal">
          <div className="modal">
            <h3 className="modal__title">Zakończ kontrolę</h3>
            {finishStep === 1 ? (
              <>
                <p className="modal__text">
                  Na liście: {session.tags.length}. Zweryfikowany: {verifiedCount}. Niezarejestrowane:{" "}
                  {session.not_found.length}.
                </p>
                <div className="modal__actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setFinishOpen(false)}>
                    Anuluj
                  </button>
                  <button
                    type="button"
                    className="btn"
                    data-testid="tags-finish-next"
                    onClick={() => setFinishStep(2)}
                  >
                    Dalej
                  </button>
                </div>
              </>
            ) : null}
            {finishStep === 2 ? (
              <>
                <label className="field">
                  <span className="field__label">Tytuł</span>
                  <input
                    className="input"
                    data-testid="tags-finish-title"
                    value={finishTitle}
                    onChange={(event) => setFinishTitle(event.target.value)}
                    placeholder="Kontrola wiosenna…"
                  />
                </label>
                <label className="field">
                  <span className="field__label">Opis (opcjonalnie)</span>
                  <textarea
                    className="input"
                    data-testid="tags-finish-description"
                    value={finishDescription}
                    onChange={(event) => setFinishDescription(event.target.value)}
                    rows={3}
                  />
                </label>
                <div className="modal__actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setFinishStep(1)}>
                    Wróć
                  </button>
                  <button
                    type="button"
                    className="btn"
                    data-testid="tags-finish-to-confirm"
                    onClick={() => {
                      if (!finishTitle.trim()) {
                        setError("Podaj tytuł sesji weryfikacji.");
                        return;
                      }
                      setError(null);
                      setFinishStep(3);
                    }}
                  >
                    Dalej
                  </button>
                </div>
              </>
            ) : null}
            {finishStep === 3 ? (
              <>
                <p className="modal__text">
                  Zapisać sesję „{finishTitle.trim()}” i zakończyć weryfikację?
                </p>
                <div className="modal__actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setFinishStep(2)}>
                    Wróć
                  </button>
                  <button
                    type="button"
                    className="btn"
                    data-testid="tags-finish-save"
                    disabled={busy}
                    onClick={() => void finishSession()}
                  >
                    Zapisz i zakończ
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </main>
  );
}
